/**
 * A photo, full size, with the keyboard.
 *
 * The gallery showed a hero and a thumbnail strip sized to a dossier panel —
 * fine for recognising a building, useless for reading a sign, a facade or a
 * roof. Opening one meant leaving the deal.
 *
 * Left/right walk the set, Escape closes, and the caption keeps the source
 * attribution with the image so a Places photo is never mistaken for one the
 * sponsor sent.
 */
import { useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import type { PropertyPhoto } from '@/types/propertyProfile'

export function PhotoLightbox({
  photos,
  index,
  onIndexChange,
  onClose,
}: {
  photos: PropertyPhoto[]
  index: number
  onIndexChange: (next: number) => void
  onClose: () => void
}) {
  const count = photos.length
  const step = useCallback(
    (delta: number) => {
      if (count === 0) return
      onIndexChange((index + delta + count) % count)
    },
    [count, index, onIndexChange],
  )

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        // Stop the dossier's own Escape handler closing the deal behind this.
        event.stopImmediatePropagation()
        onClose()
        return
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        step(1)
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        step(-1)
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose, step])

  const photo = photos[index]
  if (!photo) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex flex-col bg-black/95"
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} of ${count}: ${photo.alt}`}
      data-testid="photo-lightbox"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="flex shrink-0 items-center gap-3 px-4 py-2 text-[11px] text-stone-300">
        <span className="font-mono text-stone-500">
          {index + 1} / {count}
        </span>
        <span className="min-w-0 flex-1 truncate">{photo.alt}</span>
        {photo.source && (
          <span className="shrink-0 rounded bg-white/10 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider text-amber-200">
            {photo.source}
          </span>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close the photo"
          className="rounded p-1 text-stone-400 hover:bg-white/10 hover:text-stone-100"
        >
          <X size={16} />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-12 pb-4">
        {count > 1 && (
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label="Previous photo"
            className="absolute left-2 rounded-full bg-white/5 p-2 text-stone-300 hover:bg-white/15 hover:text-white"
          >
            <ChevronLeft size={22} />
          </button>
        )}
        {/* object-contain, never cover: a cropped facade is a different
            building, and this view exists to be looked at closely. */}
        <img
          src={photo.src}
          alt={photo.alt}
          className="max-h-full max-w-full object-contain"
        />
        {count > 1 && (
          <button
            type="button"
            onClick={() => step(1)}
            aria-label="Next photo"
            className="absolute right-2 rounded-full bg-white/5 p-2 text-stone-300 hover:bg-white/15 hover:text-white"
          >
            <ChevronRight size={22} />
          </button>
        )}
      </div>

      {count > 1 && (
        <div className="flex shrink-0 gap-1.5 overflow-x-auto px-4 pb-3">
          {photos.map((candidate, position) => (
            <button
              key={`${candidate.src}-${position}`}
              type="button"
              onClick={() => onIndexChange(position)}
              aria-label={`Show photo ${position + 1}`}
              aria-current={position === index}
              className={`h-12 w-16 shrink-0 overflow-hidden rounded border ${
                position === index ? 'border-amber-400' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img src={candidate.src} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <p className="shrink-0 px-4 pb-2 text-center text-[9px] text-stone-600">
        Left and right arrows move between photos · Escape closes
      </p>
    </div>,
    document.body,
  )
}

export default PhotoLightbox
